/* eslint-disable react-refresh/only-export-components -- context object + hook are colocated with the provider deliberately; splitting into separate files for Fast Refresh granularity isn't worth the indirection for a single app-wide context */
import { createContext, useCallback, useContext, useMemo, useState, useRef, type ReactNode } from 'react'
import type { MapRef } from 'react-map-gl/maplibre'
import type { Filters, FilterGroupKey, MapContextValue, Site, ViewMode } from '../types'

// Nothing checked. An empty selection is the *unfiltered* state, not an empty map — see
// isCategoryVisible/filterSites in hooks/useFilteredSites.ts. Starting with every box checked
// instead made the filter bar look pre-filtered on first load and gave the user no way to tell
// "I haven't chosen anything yet" from "I happen to have chosen all of them".
const DEFAULT_FILTERS: Filters = {
  categories: [],
}

const MapContext = createContext<MapContextValue | null>(null)
export { MapContext }

export function MapProvider({ children }: { children: ReactNode }) {
  const [filters, setFilters] = useState<Filters>(DEFAULT_FILTERS)
  const [selectedSite, setSelectedSite] = useState<Site | null>(null)
  const [viewMode, setViewMode] = useState<ViewMode>('map')
  const [audioGuide, setAudioGuide] = useState(false)
  // Which marker's hover card is open, held here rather than per-marker. Each marker used to own an
  // isolated `isHovered` boolean, which let two cards be open at once: decluttered markers overlap,
  // so the pointer enters the next marker's box while still inside the previous one's and the DOM
  // fires enter-on-B before leave-on-A. Touch was worse — a tap fires a synthetic mouseenter but
  // frequently no mouseleave at all, so a tapped card simply stayed open while the next one opened
  // over it. One shared id makes "one card at a time" structural instead of something every
  // handler has to cooperate to maintain.
  const [hoveredSiteId, setHoveredSiteId] = useState<string | null>(null)
  const mapRef = useRef<MapRef | null>(null)

  // The default is the empty selection, so "is default" is just "nothing checked" — any checked
  // value at all means the user has narrowed the map and the Reset control should appear.
  const isDefaultFilters = filters.categories.length === 0

  const resetFilters = useCallback(() => {
    setFilters(DEFAULT_FILTERS)
  }, [])

  const toggleFilter = useCallback((group: FilterGroupKey, value: string) => {
    setFilters(prev => {
      const current = prev[group] as string[]
      const next = current.includes(value)
        ? current.filter(v => v !== value)
        : [...current, value]
      return { ...prev, [group]: next } as Filters
    })
  }, [])

  const clearGroup = useCallback((group: FilterGroupKey) => {
    setFilters(prev => ({ ...prev, [group]: DEFAULT_FILTERS[group] }))
  }, [])

  // Memoized so a change to one slice (e.g. audioGuide) doesn't hand every consumer — including
  // every SiteMarker/SiteListCard on the map — a brand-new context value on every render.
  const value = useMemo<MapContextValue>(() => ({
    filters, toggleFilter, resetFilters, clearGroup, isDefaultFilters,
    selectedSite, setSelectedSite,
    hoveredSiteId, setHoveredSiteId,
    viewMode, setViewMode,
    audioGuide, setAudioGuide,
    mapRef,
  }), [filters, isDefaultFilters, selectedSite, hoveredSiteId, viewMode, audioGuide, toggleFilter, resetFilters, clearGroup])

  return (
    <MapContext.Provider value={value}>
      {children}
    </MapContext.Provider>
  )
}

export function useMapContext() {
  const ctx = useContext(MapContext)
  if (!ctx) throw new Error('useMapContext must be used inside MapProvider')
  return ctx
}
